import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import MortgageCompare from './MortgageCompare';
import Api from '../../Api';
import NumberFormatter from '../../NumberFormatter';
import sm from '../../StorageManager';
import InterfaceStorage from '../Site/InterfaceStorage';
import ReportQuery from './ReportQuery';
import MortgagePossibleEarn from './MortgagePossibleEarn';

export default class MortgageCompareComponent extends React.Component {

    constructor(props) {

        super(props);

        this.mortgagePrice = null;
        this.components = {
            compare: React.createRef(),
            possibleEarn: React.createRef(),
        };

        document.addEventListener(ReportQuery.CHANGED_EVENT, (e) => {

            // idk better way now
            if (e.detail.tab == 'raschet-po-stoimosti-nedvizhimosti');
                this.setMortgagePrice(e.detail.details.avgPayment);
        });
    }

    static get propTypes() {

        return {
            i18nLink: PropTypes.string.isRequired,
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    render() {

        return (<>
            <section className={ style('my-10') }>
                <MortgageCompare ref={ this.components.compare } />
            </section>

            <section className={ style('my-10') }>
                <MortgagePossibleEarn ref={ this.components.possibleEarn } hasTax={ true } />
            </section>
        </>);
    }

    //------------------------------------------------------------------------------------------------------------------
    // UPDATE
    //------------------------------------------------------------------------------------------------------------------

    componentDidMount() {

        this.updateTexts();
        this.updateData();
    }

    async updateTexts() {

        const
            texts = await this.getTexts(),
            staticTexts = {};

        Object
            .entries(texts)
            .forEach(([id, text]) => {

                if (!/\[(best|bad)\]/.test(text))
                    staticTexts[id] = text;
            });

        this
            .getCompareWidget()
            .setState({

                title: staticTexts.compareTitle,
                subtitle: staticTexts.compareSubtitle,
                tab0: staticTexts.tab0,
                tab1: staticTexts.tab1,
                tab2: staticTexts.tab2,
                tab3: staticTexts.tab3,

                textBlock: {
                    title: staticTexts.cheaperTitle,
                    subtitle: staticTexts.cheaperSubtitle,
                    paymentBadge: staticTexts.benefitBadgeLabel,
                    firstBenefit: staticTexts.firstBenefitLabel,
                    secondBenefit: staticTexts.secondBenefitLabel,
                },
    
                chartBlock: {
                    title: staticTexts.chartTitle,
                    subtitle: staticTexts.chartSubTitle,
                    mortgageLabel: staticTexts.mortgageLabel,
                    rentLabel: staticTexts.rentLabel,
                    diffLabel: staticTexts.diffLabel,
                },

            });

        this
            .getPossibleEarnWidget()
            .setState({
                
                title: staticTexts.possibleEarnTitle,
                subtitle: staticTexts.possibleEarnSubtitle,
                tab0: staticTexts.tab0,
                tab1: staticTexts.tab1,
                tab2: staticTexts.tab2,
                tab3: staticTexts.tab3,

                chartBlock: {
                    title: staticTexts.possibleEarnChartBlockTitle,
                    subtitle: staticTexts.possibleEarnChartBlockSubtitle,
                    mortgageLabel: staticTexts.mortgageLabel,
                    rentLabel: staticTexts.rentLabel,
                    taxLabel: staticTexts.possibleEarnTaxLabel ?? 'Tax',
                    diffLabel: staticTexts.possibleEarnProfitLabel ?? 'Profit',
                },

            });
    }

    async updateData() {

        const
            response = await this.getRentData(),
            currency = response.currency.symbol,
            mortgagePrice = await this.getMortgagePrice(),
            texts = await this.getTexts();

        Object
            .entries(this.getTabMapping())
            .forEach(([slug, tabId]) => {

                const
                    diff = mortgagePrice - response[slug].avg,
                    diffAbs = Math.abs(diff),
                    bestLabel = diff < 0 ? texts.mortgageLabel : texts.rentLabel,
                    badLabel = diff > 0 ? texts.mortgageLabel : texts.rentLabel,
                    textProcessing = (text) => {

                        if (text == undefined)
                            return '';
                        
                        return String(text)
                            .replace(/^\[best\]/, bestLabel)
                            .replace(/^\[bad\]/, badLabel)
                            .replaceAll('[best]', bestLabel.toLowerCase())
                            .replaceAll('[bad]', badLabel.toLowerCase());
                    };

                this
                    .getCompareWidget()
                    .updateTabData(tabId, {
                        textBlock: {
                            title: textProcessing(texts.cheaperTitle),
                            subtitle: textProcessing(texts.cheaperSubtitle),
                            firstBenefitLabel: textProcessing(texts.firstBenefitLabel),
                            secondBenefitLabel: textProcessing(texts.secondBenefitLabel),
                            paymentBadge: texts.benefitBadgeLabel,

                            paymentBenefit: new NumberFormatter(diffAbs, currency),
                            firstBenefitValue: new NumberFormatter(diffAbs * 12, currency),
                            secondBenefitValue: new NumberFormatter(diffAbs * 12 * 5, currency),
                        },
                        chartBlock: {
                            title: textProcessing(texts.chartTitle),
                            subtitle: textProcessing(texts.chartSubTitle),
                            mortgageValue: new NumberFormatter(mortgagePrice, currency),
                            rentValue: new NumberFormatter(response[slug].avg, currency),
                            diffValue: new NumberFormatter(diffAbs, currency),
                        }
                    });
                
                
                const
                    tax = parseFloat(response[slug].avg * parseFloat(texts.possibleEarnTax)),
                    profit = response[slug].avg - mortgagePrice - tax,
                    hasProfit = profit > 0;

                this
                    .getPossibleEarnWidget()
                    .updateTabData(tabId, {
                        textBlock: {
                            title: textProcessing(hasProfit ? texts.possibleEarnTextPositiveBlockTitle : texts.possibleEarnTextNegativeBlockTitle),
                            subtitle: textProcessing(hasProfit ? texts.possibleEarnTextPositiveBlockSubtitle : texts.possibleEarnTextNegativeBlockSubtitle),
                            firstBenefit: textProcessing(hasProfit ? texts.firstBenefitLabel : texts.firstLesionLabel),
                            secondBenefit: textProcessing(hasProfit ? texts.secondBenefitLabel : texts.secondLesionLabel),
                            paymentBadge: textProcessing(hasProfit ? texts.benefitBadgeLabel : texts.lesionBadgeLabel),

                            paymentBenefit: new NumberFormatter(profit, currency),
                            firstBenefitValue: new NumberFormatter(profit * 12, currency),
                            secondBenefitValue: new NumberFormatter(profit * 12 * 5, currency),
                        },
                        chartBlock: {
                            mortgageValue: new NumberFormatter(mortgagePrice, currency),
                            rentValue: new NumberFormatter(response[slug].avg, currency),
                            taxValue: new NumberFormatter(tax, currency),
                            diffValue: new NumberFormatter(profit, currency),
                        }
                    });
            });
    }

    //------------------------------------------------------------------------------------------------------------------
    // GETTERS & SETTERS
    //------------------------------------------------------------------------------------------------------------------

    setMortgagePrice(value) {

        this.mortgagePrice = value;
        this.updateData();
    }

    getMortgagePricePromise() {

        return new Promise((resolve) => {

            if (this.mortgagePrice != null)
                resolve(this.mortgagePrice);

            const checkInterval = setInterval(() => {

                if (this.mortgagePrice != null) {

                    clearInterval(checkInterval);
                    resolve(this.mortgagePrice);
                }
            }, 250);
        });
    }

    getMortgagePrice() {

        return this.getMortgagePricePromise();
    }

    getCompareWidget() {

        return this.components.compare.current;
    }

    getPossibleEarnWidget() {

        return this.components.possibleEarn.current;
    }

    getTexts() {

        if (this.textPromise == undefined)
            this.textPromise = this.getTextsPromise();

        return this.textPromise;
    }

    getTextsPromise() {

        return new Promise((resolve) => {

            sm
                .getFrom(this.props.i18nLink)
                .then(() => {

                    resolve({
            
                        compareTitle: sm.get(InterfaceStorage.CALCULATOR, 'compareOffersTitle', false, false),
                        compareSubtitle: sm.get(InterfaceStorage.COUNTRY, 'compareOffersSubtitle'),
            
                        cheaperTitle: sm.get(InterfaceStorage.COUNTRY, 'profitBlockTitle'),
                        cheaperSubtitle: sm.get(InterfaceStorage.COUNTRY, 'profitBlockSubtitle'),
        
                        chartTitle: sm.get(InterfaceStorage.COUNTRY, 'newChartBlockTitle', false, false)
                            ?? sm.get(InterfaceStorage.CALCULATOR, 'newChartBlockTitle', false, false),
                        chartSubTitle: sm.get(InterfaceStorage.COUNTRY, 'chartBlockSubTitle'),
                        
                        tab0: sm.get(InterfaceStorage.COUNTRY, 'rentOneRoomCenter', false, false)
                            ?? 'One-room apartment in the center',
                        tab1: sm.get(InterfaceStorage.COUNTRY, 'rentOneRoomOutskirts', false, false)
                            ?? 'One-room apartment on the outskirts',
                        tab2: sm.get(InterfaceStorage.COUNTRY, 'rentThreeRoomCenter', false, false)
                            ?? 'Three-room apartment in the center',
                        tab3: sm.get(InterfaceStorage.COUNTRY, 'rentThreeRoomOutskirts', false, false)
                            ?? 'Three-room apartment on the outskirts',
                        
                        mortgageLabel: sm.get(InterfaceStorage.COUNTRY, 'mortgageLabel', false, false) ?? 'Mortgage',
                        rentLabel: sm.get(InterfaceStorage.COUNTRY, 'rentLabel', false, false) ?? 'Rent',
                        diffLabel: sm.get(InterfaceStorage.COUNTRY, 'chartBlockDiffLabel'),
            
                        benefitBadgeLabel: sm.get(InterfaceStorage.COUNTRY, 'profitTagText'),
                        firstBenefitLabel: sm.get(InterfaceStorage.COUNTRY, 'profitPerOneYearLabel'),
                        secondBenefitLabel: sm.get(InterfaceStorage.COUNTRY, 'profitPerFiveYearLabel'),
            
                        lesionBadgeLabel: sm.get(InterfaceStorage.COUNTRY, 'lesionTagText', false, false),
                        firstLesionLabel: sm.get(InterfaceStorage.COUNTRY, 'lesionPerOneYearLabel', false, false),
                        secondLesionLabel: sm.get(InterfaceStorage.COUNTRY, 'lesionPerFiveYearLabel', false, false),

                        possibleEarnTitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTitle', false, false),
                        possibleEarnSubtitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnSubtitle', false, false),
                        possibleEarnTax: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTax', false, false),
                        possibleEarnTaxLabel: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTaxLabel', false, false),
                        possibleEarnTextPositiveBlockTitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTextPositiveBlockTitle', false, false),
                        possibleEarnTextPositiveBlockSubtitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTextPositiveBlockSubtitle', false, false),
                        possibleEarnTextNegativeBlockTitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTextNegativeBlockTitle', false, false),
                        possibleEarnTextNegativeBlockSubtitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnTextNegativeBlockSubtitle', false, false),
                        possibleEarnChartBlockTitle: sm.get(InterfaceStorage.CALCULATOR, 'possibleEarnChartBlockTitle', false, false)
                            ?? sm.get(InterfaceStorage.COUNTRY, 'possibleEarnChartBlockTitle', false, false),
                        possibleEarnChartBlockSubtitle: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnChartBlockSubtitle', false, false),
                        possibleEarnProfitLabel: sm.get(InterfaceStorage.COUNTRY, 'possibleEarnProfitLabel', false, false),
                    });
                });
        });
    }

    getTabMapping() {
        
        return {
            'Rent one-room apartment in the center': 0,
            'Rent one-room apartment on the outskirts': 1,
            'Three-room apartment for rent in the center': 2,
            'Rent a three-room apartment on the outskirts': 3,
        };
    }

    getRentData() {

        if (this.rentDataPromise == undefined)
            this.rentDataPromise = this.getRentDataPromise();

        return this.rentDataPromise;
    }

    async getRentDataPromise() {

        return await Api.fetch(`${Api.origin}/api/v2/price/rent-price.json?country=${document.body.dataset.locale}`);
    }
}